import { selectAll, select } from 'd3';
import type { TreeNode, NavigationResult } from '../types';
import type { HierarchyPointNode } from 'd3';

export function checkIfNodeInViewport(rect: DOMRect, containerRect: DOMRect): boolean {
    return (
        rect.top >= containerRect.top &&
        rect.left >= containerRect.top &&
        rect.bottom <= containerRect.bottom &&
        rect.right <= containerRect.right
    );
}

function getAllNodeSiblings(d: HierarchyPointNode<TreeNode>): HierarchyPointNode<TreeNode>[] | null {
    if (d.parent && d.parent.children.length > 1) {
        return d.parent.children;
    } else {
        return null;
    }
}

export function toggleTabIndex(d: HierarchyPointNode<TreeNode>): void {
    if (d.data.id) {
        const prevFocus = selectAll<SVGSVGElement, TreeNode>(`[tabindex="0"][class="node-text"]`);
        if (prevFocus) {
            prevFocus.attr('tabindex', '-1');
            select<SVGGElement, TreeNode>(`#n-${d.data.id} .node-text`).attr('tabindex', '0');
        }
    }
}

export function toggleActiveFocus(d: HierarchyPointNode<TreeNode>): void {
    if (d.data.id) {
        toggleTabIndex(d);
        select<SVGSVGElement, TreeNode>(`#n-${d.data.id} .node-text`).node().focus();
    }
}

const findNextSibling = (d: HierarchyPointNode<TreeNode>, diff: 1 | -1): HierarchyPointNode<TreeNode> | undefined => {
    if (d != null && d.parent && d.parent.parent) {
        const parent = d.parent;
        const grandParent = parent.parent;
        const index = grandParent.children.indexOf(parent);
        let newIndex = index + diff;
        let siblingParentChildren: HierarchyPointNode<TreeNode>[] | undefined;
        while (!siblingParentChildren && newIndex >= 0 && newIndex < grandParent.children.length) {
            if (grandParent.children[newIndex].children) {
                siblingParentChildren = grandParent.children[newIndex].children;
            }
            newIndex += diff;
        }
        let siblingNode: HierarchyPointNode<TreeNode> | undefined;
        if (siblingParentChildren) {
            siblingNode = diff > 0 ? siblingParentChildren[0] : siblingParentChildren[siblingParentChildren.length - 1];
        }
        return siblingNode;
    }
};

const getParentOrToggle = (d: HierarchyPointNode<TreeNode>, result: NavigationResult): NavigationResult => {
    if (d.parent && (d.data.children.length == 0 || d.data._children)) {
        result.focus = d.parent;
    } else if (d.data.children.length > 0 && d.children) {
        result.toggle = d;
    }
    return result;
};

const getChildOrToggle = (d: HierarchyPointNode<TreeNode>, result: NavigationResult): NavigationResult => {
    if (d.data.children.length > 0 && d.data._children) {
        result.toggle = d;
    } else if (d.data.children.length > 0 && d.children) {
        result.focus = d.children[0];
    }
    return result;
};

const getSiblingToToggle = (
    d: HierarchyPointNode<TreeNode>,
    result: NavigationResult,
    key: string
): NavigationResult => {
    const nodeSiblings = getAllNodeSiblings(d) || [];
    const index = nodeSiblings.findIndex((node) => node.data.name === d.data.name);
    const diff = key === 'ArrowDown' ? 1 : -1;
    const newIndex = index + diff;

    if (nodeSiblings[newIndex] !== undefined) {
        result.focus = nodeSiblings[newIndex];
    } else {
        result.focus = findNextSibling(d, diff);
    }
    return result;
};

export const handleNavigationKeys = (key: string, d: HierarchyPointNode<TreeNode>): NavigationResult => {
    const result: NavigationResult = {};
    switch (key) {
        case 'ArrowLeft':
            getParentOrToggle(d, result);
            break;
        case 'ArrowRight':
            getChildOrToggle(d, result);
            break;
        case 'ArrowUp':
        case 'ArrowDown':
            getSiblingToToggle(d, result, key);
            break;
    }
    return result;
};
